import { useCallback, useEffect, useState } from "react";
import { RamCalendarEntry } from "../interfaces";
import useLiveApi from "./use-live-api";
import { usePagination } from "./usePagination";

interface CalendarRecordingsApi {
  data: RamCalendarEntry[];
  loading: boolean;
  error: boolean;
  nextPage: () => Promise<void>;
  prevPage: () => Promise<void>;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const fetchItems = async (
  apiGatewayFetch: (
    url: string,
    method: string,
    body?: string
  ) => Promise<Response>,
  vehicleId: string,
  limit?: number,
  paginationToken?: string
) => {
  let url = `/calendar/${vehicleId}`;
  if (limit) url += `?limit=${limit}`;
  if (paginationToken) url += `?token=${paginationToken}`;
  const response = await apiGatewayFetch(url, "get");
  if (response.status !== 200) {
    throw new Error(response.status.toString());
  }
  return await response.json();
};

export const useCalendarRecordingsApi = (
  vehicleId: string | null,
  limit?: number
): CalendarRecordingsApi => {
  const { apiGatewayFetch } = useLiveApi();
  const [data, setData] = useState<RamCalendarEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [pageTokens, setPageTokens] = useState<(string | null)[]>([""]); // page 1 has no token
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

  const loadPage = useCallback(
    async (paginationToken?: string) => {
      if (!vehicleId) {
        return;
      }
      setLoading(true);
      setError(false);
      try {
        const data = await fetchItems(
          apiGatewayFetch,
          vehicleId,
          limit,
          paginationToken
        );
        setData(data.items as RamCalendarEntry[]);
        setNextToken(data.paginationToken ?? null);
        setLoading(false);
      } catch (e) {
        setError(e.message || "An error occurred");
        setLoading(false);
      }
    },
    [apiGatewayFetch, vehicleId, limit]
  );

  const { nextPage, prevPage, hasNextPage, hasPrevPage } = usePagination({
    loadPage,
    nextToken,
    currentPageIndex,
    pageTokens,
    setPageTokens,
    setCurrentPageIndex,
  });

  useEffect(() => {
    if (vehicleId) {
      loadPage();
    }
    setPageTokens([""]);
    setCurrentPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this once
  }, [vehicleId, limit]);

  return { data, nextPage, prevPage, hasNextPage, hasPrevPage, loading, error };
};
